import { useQuery } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PageMeta from '../../components/common/PageMeta';
import { useScopedQueryKey } from '../../hooks/useScopedQueryKey';
import {
  ListBody,
  Segmented,
  Table,
  TableSkeletonRows,
  TBody,
} from '../../ui/v2';
import {
  fetchReservationsIntegrationStatus,
  reservationsStatusHasActiveConnection,
} from '../../utils/reservationsIntegrationStatus';
import OpenTableReservationsView from './OpenTableReservationsView';
import ReservationLinksView from './ReservationLinksView';
import { ReservationsFrame } from './ReservationsHeader';
import {
  DEFAULT_LINKS_ORDERING,
  fetchReservationLinksPage,
  RESERVATIONS_PAGE_SIZE,
} from './reservationModel';
import {
  reservationsPath,
  resolveReservationsRoute,
  type ReservationsTab,
} from './reservationsRoutes';

const VIEW_LABEL: Record<ReservationsTab, string> = {
  opentable: 'OpenTable reservations',
  links: 'Reservation links',
};

export default function ReservationsTable() {
  const location = useLocation();
  const navigate = useNavigate();
  const resolvedRoute = resolveReservationsRoute(location.pathname);

  const { data: reservationsStatus, isPending: otStatusPending } = useQuery({
    queryKey: useScopedQueryKey('reservations-integration-status'),
    queryFn: fetchReservationsIntegrationStatus,
  });
  const hasOpenTableConnection =
    reservationsStatusHasActiveConnection(reservationsStatus);
  const { data: linksResult, isPending: linksPending } = useQuery({
    queryKey: useScopedQueryKey(
      'reservation-links',
      1,
      RESERVATIONS_PAGE_SIZE,
      DEFAULT_LINKS_ORDERING,
    ),
    queryFn: () =>
      fetchReservationLinksPage(
        1,
        RESERVATIONS_PAGE_SIZE,
        DEFAULT_LINKS_ORDERING,
      ),
  });

  const tabPreferenceReady = !otStatusPending && !linksPending;
  const hasReservationLinks = (linksResult?.totalCount ?? 0) > 0;
  const preferLinksTab =
    tabPreferenceReady && !hasOpenTableConnection && hasReservationLinks;
  const preferredTab: ReservationsTab = preferLinksTab ? 'links' : 'opentable';
  const activeTab: ReservationsTab | null = resolvedRoute.tab
    ? resolvedRoute.tab
    : tabPreferenceReady
      ? preferredTab
      : null;

  useEffect(() => {
    if (resolvedRoute.redirectTo) {
      navigate(
        { pathname: resolvedRoute.redirectTo, search: location.search },
        { replace: true },
      );
      return;
    }
    if (resolvedRoute.tab || !tabPreferenceReady) return;
    navigate(
      { pathname: reservationsPath(preferredTab), search: location.search },
      { replace: true },
    );
  }, [
    location.search,
    navigate,
    preferredTab,
    resolvedRoute.redirectTo,
    resolvedRoute.tab,
    tabPreferenceReady,
  ]);

  const openTab = (tab: ReservationsTab) => {
    const path = reservationsPath(tab);
    if (path === location.pathname) return;
    navigate({ pathname: path, search: location.search });
  };

  const viewOrder: ReservationsTab[] = preferLinksTab
    ? ['links', 'opentable']
    : ['opentable', 'links'];
  const viewSwitch = (
    <Segmented
      aria-label="Reservations view"
      items={viewOrder.map((key) => ({ key, label: VIEW_LABEL[key] }))}
      value={activeTab as ReservationsTab}
      onChange={openTab}
      className="shrink-0"
    />
  );

  return (
    <>
      <PageMeta
        title="AskArthur | Reservations"
        description="AskArthur Reservation Page"
      />

      <ReservationsFrame viewSwitch={viewSwitch}>
        {activeTab === 'opentable' ? (
          <OpenTableReservationsView />
        ) : activeTab === 'links' ? (
          <ReservationLinksView />
        ) : (
          <ListBody>
            <p role="status" className="sr-only">
              Loading reservations…
            </p>
            <Table>
              <TBody>
                <TableSkeletonRows cols={5} />
              </TBody>
            </Table>
          </ListBody>
        )}
      </ReservationsFrame>
    </>
  );
}
